import React from 'react';

import styled from 'styled-components';

import { BackToTopButton } from 'components/no-auth/common/BackToTopButton/BackToTopButton';
import { BlogTile } from 'components/no-auth/common/BlogTile/BlogTile';
import { DateAndLocation } from 'components/no-auth/common/BlogTile/DateAndLocation';
import { Breadcrumbs } from 'components/no-auth/common/Breadcrumbs/Breadcrumbs';
import { PromoBadge } from 'components/no-auth/common/PromoBadge/PromoBadge';
import { PromoDangerouslyHtml } from 'components/no-auth/common/PromoDangerouslyHtml/PromoDangerouslyHtml';
import { PromoRow } from 'components/no-auth/common/PromoRow/PromoRow';
import { PromoSectionContainer } from 'components/no-auth/common/PromoSectionContainer';
import HeadingSection from 'components/no-auth/common/sections/HeadingSection';
import { QuoteSectionV2 } from 'components/no-auth/common/sections/QuoteSectionV2';
import { SocialMediaSection } from 'components/no-auth/common/sections/SocialMediaSection';
import { TilesSection } from 'components/no-auth/common/sections/TilesSection';
import NewsLayoutV2 from 'components/no-auth/news/NewsLayoutV2';

import { getLangCountryPrefix } from 'helpers/utils';

import promoMedia from 'theme/promoMedia';

import { TextComponent } from '../../components/no-auth/common/sections/styles';

const BadgeItem = styled.div`
    margin-right: 8px;

    ${promoMedia.tablet744} {
        margin-right: 12px;
    }

    ${promoMedia.desktop1440} {
        margin-right: 16px;
    }

    &:last-child {
        margin-right: 0;
    }
`;
const Description = styled.div`
    margin-bottom: 32px;

    ${promoMedia.tablet744} {
        margin-bottom: 48px;
    }

    ${promoMedia.desktop1440} {
        max-width: 888px;
        margin: 0 auto 64px;
    }
`;
const Illustration = styled.img`
    margin-bottom: 24px;
    max-width: 100%;
    display: block;

    ${promoMedia.tablet744} {
        margin-bottom: 32px;
    }

    ${promoMedia.desktop1440} {
        max-width: 704px;
        margin: 0 auto 40px;
    }
`;
const VideoContainer = styled.div`
    position: relative;
    aspect-ratio: 16/9;
    width: 100%;
    margin-bottom: 32px;

    & > div {
        padding-top: calc(100% / (16 / 9));
    }

    ${promoMedia.tablet744} {
        margin-bottom: 48px;
    }

    ${promoMedia.desktop1440} {
        max-width: 704px;
        margin: 0 auto 64px;
    }

    iframe {
        height: 100%;
        position: absolute;
        left: 0;
        max-height: 100%;
        max-width: 100%;
        top: 0;
        width: 100%;
    }
`;
const BlogTiles = styled.div`
    margin-left: -10px;
    margin-right: -10px;
`;
const NewsArticlePageV2 = (props) => {
    const url = `${getLangCountryPrefix(props.lang, props.country)}/${props.stateOptions?.section}`;

    const breadcrumbs = [{ name: props.landing?.allArticle, url: url }, { name: props.selectedArticle?.title }];

    return (
        <>
            {props.selectedArticle && props.landing && props.social_section && props.articles && (
                <PromoSectionContainer>
                    {props.landing.allArticle && props.selectedArticle.title && <Breadcrumbs breadcrumbsItems={breadcrumbs} />}
                    {props.social_section.icons && <SocialMediaSection socialIcons={props.social_section.icons} noBottomMargin />}
                    {props.selectedArticle.title && <HeadingSection title={props.selectedArticle.title} titleTag={'h1'} />}
                    <PromoRow flex flexWrap align="center" indent="adaptive-32-48-64" rowGap="16px">
                        {props.selectedArticle.date && <DateAndLocation date={props.selectedArticle.date} textVariant="subheader" />}

                        {Array.isArray(props.selectedArticle.categories) &&
                            props.selectedArticle.categories
                                .filter((item) => item !== null)
                                .map((item) => {
                                    return (
                                        <BadgeItem key={item.id}>
                                            <PromoBadge caption={item.title} isInactive />
                                        </BadgeItem>
                                    );
                                })}
                    </PromoRow>

                    {props.selectedArticle.img && (
                        <Illustration
                            src={props.selectedArticle.img?.url}
                            alt={props.selectedArticle.img?.alternativeText}
                            loading={'lazy'}
                            decoding={'async'}
                        />
                    )}

                    {props.selectedArticle.video_url && (
                        <VideoContainer>
                            <div>
                                <iframe
                                    src={props.selectedArticle.video_url}
                                    width="100%"
                                    height="100%"
                                    frameBorder="0"
                                    allow="autoplay; encrypted-media;accelerometer; encrypted-media; gyroscope; picture-in-picture"
                                    allowFullScreen="1"
                                    loading="lazy"
                                />
                            </div>
                        </VideoContainer>
                    )}

                    {props.selectedArticle.description && (
                        <Description>
                            <TextComponent>
                                <PromoDangerouslyHtml color={'baseColor'} tag={'div'} dangerouslyHtml={props.selectedArticle.description} />
                            </TextComponent>
                        </Description>
                    )}

                    {/* Quote tile */}
                    {props.selectedArticle.quote_section && props.selectedArticle.quote_section.length > 0 && (
                        <QuoteSectionV2 data={props.selectedArticle.quote_section} />
                    )}

                    {props.selectedArticle.description2 && (
                        <Description>
                            <TextComponent>
                                <PromoDangerouslyHtml color={'baseColor'} tag={'div'} dangerouslyHtml={props.selectedArticle.description2} />
                            </TextComponent>
                        </Description>
                    )}

                    {props.selectedArticle.tiles_section && props.selectedArticle.tiles_section.length > 0 && (
                        <TilesSection data={props.selectedArticle.tiles_section} />
                    )}

                    {props.landing.latestPosts && <HeadingSection title={props.landing.latestPosts} />}

                    {props.articles.data && (
                        <BlogTiles>
                            <PromoRow indent="adaptive-32-48-64">
                                {Array.isArray(props.articles.data) &&
                                    props.articles.data
                                        .filter((item, index) => item !== null && index < 2)
                                        .map((item) => {
                                            return (
                                                <BlogTile
                                                    key={item.id}
                                                    date={item.date}
                                                    location={item.location}
                                                    text={item.preview_text}
                                                    categories={item.categories}
                                                    title={item.title}
                                                    img={item.img}
                                                    link={`${url}/${item.slug}`}
                                                />
                                            );
                                        })}
                            </PromoRow>
                        </BlogTiles>
                    )}
                    {props.landing.backToTopBtn && (
                        <PromoRow indent="adaptive-32-48-64">
                            <BackToTopButton caption={props.landing.backToTopBtn} />
                        </PromoRow>
                    )}
                </PromoSectionContainer>
            )}
        </>
    );
};
export default NewsLayoutV2(NewsArticlePageV2);
